/**
 * Variant Selection Component
 */
(function () {
  "use strict";

  const VariantSelection = {
    init() {
      // Handle variant button clicks (for homepage PDP and product forms)
      document.querySelectorAll("[data-variant-button]").forEach((button) => {
        button.addEventListener("click", (e) => {
          e.preventDefault();
          const variantId = button.dataset.variantId;
          if (!variantId) return;

          const form =
            button.closest("form") ||
            document.querySelector("[data-product-form]");
          if (!form) return;

          this.selectVariant(variantId, form);
        });
      });

      // Handle variant selector changes (dropdowns, etc.)
      document
        .querySelectorAll("[data-variant-selector]")
        .forEach((selector) => {
          selector.addEventListener("change", (e) => {
            const form =
              selector.closest("form") ||
              document.querySelector("[data-product-form]");
            if (!form) return;

            const selectedOptions = {};
            form.querySelectorAll("[data-variant-selector]").forEach((sel) => {
              if (sel.name && sel.value) {
                selectedOptions[sel.name] = sel.value;
              }
            });

            // Find matching variant
            const variantId = this.findVariantId(form, selectedOptions);
            if (variantId) {
              this.selectVariant(variantId, form);
            }
          });
        });
    },

    findVariantId(form, selectedOptions) {
      const variantSelect = form.querySelector("[data-variant-select]");
      if (!variantSelect) return null;

      // Get all variants from the select element
      const variants = Array.from(variantSelect.options).map((opt) => ({
        id: opt.value,
        title: opt.text,
      }));

      // Find variant that matches all selected options
      for (const variant of variants) {
        let matches = true;
        for (const [optionName, optionValue] of Object.entries(
          selectedOptions
        )) {
          if (!variant.title.includes(optionValue)) {
            matches = false;
            break;
          }
        }
        if (matches) {
          return variant.id;
        }
      }

      return null;
    },

    selectVariant(variantId, form) {
      if (!variantId || !form) return;

      // Update hidden select element
      const variantSelect = form.querySelector("[data-variant-select]");
      if (variantSelect) {
        variantSelect.value = variantId;
        variantSelect.dispatchEvent(new Event("change", { bubbles: true }));
      }

      // Update variant button states within the form
      form.querySelectorAll("[data-variant-button]").forEach((button) => {
        const isSelected = button.dataset.variantId === variantId;

        // Update button background and hover states
        if (isSelected) {
          button.classList.add("bg-gum", "text-white");
          button.classList.remove("hover:bg-pink");
        } else {
          button.classList.remove("bg-gum", "text-white");
          button.classList.add("hover:bg-pink");
        }

        // Update nested h4 text colors
        const h4Elements = button.querySelectorAll("h4");
        h4Elements.forEach((h4) => {
          if (isSelected) {
            h4.classList.add("!text-white");
          } else {
            h4.classList.remove("!text-white");
          }
        });

        // Update compare price text color
        const comparePrice = button.querySelector(
          "[data-variant-compare-price]"
        );
        if (comparePrice) {
          if (isSelected) {
            comparePrice.classList.remove("text-dark-gray");
            comparePrice.classList.add("text-white/60");
          } else {
            comparePrice.classList.remove("text-white/60");
            comparePrice.classList.add("text-dark-gray");
          }
        }

        // Update per-pack text color
        const perPack = button.querySelector("[data-variant-per-pack]");
        if (perPack) {
          if (isSelected) {
            perPack.classList.remove("text-gum");
            perPack.classList.add("text-gold");
          } else {
            perPack.classList.remove("text-gold");
            perPack.classList.add("text-gum");
          }
        }

        // Update "Free Shipping" text color
        const freeShipping = button.querySelector("[data-free-shipping]");
        if (freeShipping) {
          if (isSelected) {
            freeShipping.classList.remove("!text-gum");
            freeShipping.classList.add("!text-white");
          } else {
            freeShipping.classList.remove("!text-white");
            freeShipping.classList.add("!text-gum");
          }
        }

        // Update discount badge styling
        const discountBadge = button.querySelector(".bg-pink.rounded-4xl");
        if (discountBadge) {
          if (isSelected) {
            discountBadge.classList.add("border-[1px]", "border-white");
            discountBadge.classList.remove(
              "group-hover:bg-gum",
              "group-hover:text-white",
              "transition-all"
            );
          } else {
            discountBadge.classList.remove("border-[1px]", "border-white");
            discountBadge.classList.add(
              "group-hover:bg-gum",
              "group-hover:text-white",
              "transition-all"
            );
          }
        }
      });

      // Update prices and availability from data attributes
      const variantData = this.getVariantData(variantId, form);
      if (variantData) {
        this.updatePrices(variantData, form);
        this.updateAvailability(variantData, form);
        this.updateMedia(variantData);
      }

      /**
       * Update the URL without reloading.
       *
       * CHANGED 25 OCT 2026
       *
       * This used to write the option name and value as the query string:
       *
       *     url.searchParams.set(optionName, optionValue);   // ?Size=12+Packs
       *
       * which produced a URL that nothing on the server could read back.
       * sections/homepage-pdp.liquid had a hand rolled parser for it that was
       * broken in four separate ways, and that parser has been deleted.
       *
       * It now writes Shopify's own ?variant=<id>, which:
       *
       *   - product.selected_variant reads natively, with no parsing
       *   - identifies exactly one variant, so flavor and size are both
       *     carried by a single unambiguous value
       *   - is the format paid traffic will arrive on, so a shared URL and
       *     an ad link take the identical code path
       *
       * Stale option params left over from an older link are stripped so the
       * URL does not accumulate two competing descriptions of one variant.
       */
      const url = new URL(window.location);
      url.searchParams.set("variant", variantId);

      form.querySelectorAll("[data-variant-data]").forEach((el) => {
        if (el.dataset.optionName) url.searchParams.delete(el.dataset.optionName);
      });

      window.history.replaceState({}, "", url.toString());
    },

    getVariantData(variantId, form) {
      const variantDataEl = form.querySelector(
        `[data-variant-data="${variantId}"]`
      );
      if (!variantDataEl) return null;

      return {
        id: variantId,
        price: variantDataEl.dataset.price,
        comparePrice: variantDataEl.dataset.comparePrice,
        available: variantDataEl.dataset.available === "true",
        image: variantDataEl.dataset.image,
        // Added 25 Oct 2026 so updateMedia can jump to a real slide instead
        // of overwriting one. Empty when the variant has no featured image.
        mediaId: variantDataEl.dataset.mediaId || "",
      };
    },

    updatePrices(variantData, form) {
      const scope = form || document;

      // Update price displays
      scope.querySelectorAll("[data-variant-price]").forEach((el) => {
        if (el.dataset.variantId === variantData.id || !el.dataset.variantId) {
          el.textContent = this.formatMoney(variantData.price);
        }
      });

      // Update compare at price
      scope.querySelectorAll("[data-variant-compare-price]").forEach((el) => {
        if (el.dataset.variantId === variantData.id || !el.dataset.variantId) {
          if (
            variantData.comparePrice &&
            parseInt(variantData.comparePrice) > parseInt(variantData.price)
          ) {
            el.textContent = this.formatMoney(variantData.comparePrice);
            el.style.display = "";
          } else {
            el.style.display = "none";
          }
        }
      });

      // Update per-pack pricing
      scope.querySelectorAll("[data-variant-per-pack]").forEach((el) => {
        if (el.dataset.variantId === variantData.id || !el.dataset.variantId) {
          const packCount = parseInt(el.dataset.packCount) || 1;
          const perPack = parseInt(variantData.price) / packCount;
          el.textContent = this.formatMoney(perPack) + " Per Pack";
        }
      });
    },

    updateAvailability(variantData, form) {
      const scope = form || document;

      // Update add to cart button
      const addToCartBtn = scope.querySelector("[data-add-to-cart]");
      if (addToCartBtn) {
        if (variantData.available) {
          addToCartBtn.disabled = false;
          addToCartBtn.classList.remove("opacity-50", "cursor-not-allowed");
          const priceText = addToCartBtn.querySelector("[data-button-price]");
          if (priceText) {
            priceText.textContent = this.formatMoney(variantData.price);
          }
        } else {
          addToCartBtn.disabled = true;
          addToCartBtn.classList.add("opacity-50", "cursor-not-allowed");
          const soldOutText = addToCartBtn.querySelector(
            "[data-button-sold-out]"
          );
          if (soldOutText) {
            soldOutText.textContent = "Sold out";
          }
        }
      }

      // Update variant button availability
      scope.querySelectorAll("[data-variant-button]").forEach((button) => {
        if (button.dataset.variantId === variantData.id) {
          if (variantData.available) {
            button.classList.remove("opacity-50", "cursor-not-allowed");
            button.disabled = false;
          } else {
            button.classList.add("opacity-50", "cursor-not-allowed");
            button.disabled = true;
          }
        }
      });
    },

    /**
     * CHANGED 25 OCT 2026
     *
     * This used to overwrite the src of the carousel's FIRST slide with the
     * variant image. That is destructive: slide one permanently stops being
     * the photo its thumbnail shows, so the gallery and its thumbnails
     * disagree from the first variant click onward, and the damage persists
     * for the rest of the session.
     *
     * It now jumps the carousel to the slide that already holds the variant's
     * featured media, which is what the gallery is for. Nothing is mutated.
     */
    updateMedia(variantData) {
      if (!variantData || !variantData.mediaId) return;
      if (!window.MediaCarousel || !window.MediaCarousel.jumpToMedia) return;

      document.querySelectorAll("[data-media-carousel]").forEach((carousel) => {
        window.MediaCarousel.jumpToMedia(carousel, variantData.mediaId);
      });
    },

    formatMoney(cents) {
      const amount = parseInt(cents) / 100;
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(amount);
    },
  };

  window.VariantSelection = VariantSelection;
})();